/**
 * Глобальный словарь прав групп пользователей
 */

export interface GroupCategory {
  name: string;
  code: number;
  description?: string;
}

export interface GroupCategoryRange {
  name: string;
  minCode: number;
  maxCode: number;
  description?: string;
}

/**
 * Категории групп с диапазонами кодов
 */
export const GROUP_CATEGORIES: Record<string, GroupCategoryRange> = {
  USERS: {
    name: 'Пользователь',
    minCode: 0,
    maxCode: 99,
    description: 'Обычный пользователь с базовыми правами',
  },
  EXTENDED_USERS: {
    name: 'Пользователь с расширенными правами',
    minCode: 100,
    maxCode: 199,
    description: 'Пользователь с дополнительными возможностями',
  },
  ADMINS: {
    name: 'Администратор',
    minCode: 200,
    maxCode: 299,
    description: 'Администратор с полными правами',
  },
} as const;

/**
 * Категории групп с фиксированными кодами
 */
export const GROUP_USERS: Record<string, GroupCategory> = {
  USERS: {
    name: 'Пользователь',
    code: 1,
    description: 'Обычный пользователь с базовыми правами',
  },
  EMPLOYEES: {
    name: 'Пользователи (сотрудники)',
    code: 2,
    description: 'Сотрудники с базовыми правами пользователя',
  },
  EXTENDED_USERS: {
    name: 'Пользователь с расширенными правами',
    code: 101,
    description: 'Пользователь с дополнительными возможностями',
  },
  ADMINS: {
    name: 'Администратор',
    code: 201,
    description: 'Администратор с полными правами',
  },
} as const;

/**
 * Проверка принадлежности кода к категории
 */
export function isCodeInCategory(code: number, category: keyof typeof GROUP_CATEGORIES): boolean {
  const cat = GROUP_CATEGORIES[category];
  return code >= cat.minCode && code <= cat.maxCode;
}

/**
 * Получение категории по коду
 */
export function getCategoryByCode(code: number): GroupCategory | null {
  for (const category of Object.values(GROUP_USERS)) {
    if (category.code === code) {
      return category;
    }
  }
  return null;
}

/**
 * Проверка, имеет ли пользователь расширенные права
 */
export function hasExtendedRights(code: number): boolean {
  return isCodeInCategory(code, 'EXTENDED_USERS') || isCodeInCategory(code, 'ADMINS');
}

/**
 * Проверка, является ли пользователь администратором
 */
export function isAdmin(code: number): boolean {
  return isCodeInCategory(code, 'ADMINS');
}

/**
 * Проверка, является ли пользователь администратором (проверка массива групп)
 */
export function isUserAdmin(groups: number[] | undefined): boolean {
  if (!groups || groups.length === 0) return false;
  return groups.some(code => isAdmin(code));
}

/**
 * Проверка, имеет ли пользователь расширенные права (проверка массива групп)
 */
export function userHasExtendedRights(groups: number[] | undefined): boolean {
  if (!groups || groups.length === 0) return false;
  return groups.some(code => hasExtendedRights(code));
}

/**
 * Валидация кода группы
 */
export function isValidGroupCode(code: number): boolean {
  return getCategoryByCode(code) !== null;
}

/**
 * Получение всех категорий в виде массива
 */
export function getAllCategories(): GroupCategory[] {
  return Object.values(GROUP_USERS);
}

/**
 * Получение всех доступных кодов групп
 */
export function getAllGroupCodes(): number[] {
  return Object.values(GROUP_USERS).map(cat => cat.code);
}




/**
 * Получение названия категории по коду
 */
export function getCategoryName(code: number): string {
  const category = getCategoryByCode(code);
  return category?.name || 'Неизвестная группа';
}
